import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertCircle, Circle, Clock } from "lucide-react";
import { ProcessCard, WaitList, groupTodo } from "@/components/PainelGroups";
import { WeekAgenda, Compliance } from "@/components/PainelAgenda";
import { FilaDialog } from "@/components/GestaoDialogs";
import { remindAction } from "@/components/RemindPreview";
import { Arrivals, useArrivalToast } from "@/components/PainelArrivals";
import { HoraLocalDialog } from "@/components/HoraLocalDialog";

const WD = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MO = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const hello = () => { const h = new Date().getHours(); return h < 13 ? "Bom dia" : h < 20 ? "Boa tarde" : "Boa noite"; };
const ALERTS = [
  ["urgent", "urgentes", "CPCV e escrituras em risco esta semana", AlertCircle, "border-red-200 bg-red-50", "text-red-700", "text-red-600"],
  ["today", "para hoje", "ações a fazer hoje", Circle, "border-amber-200 bg-amber-50", "text-amber-800", "text-amber-500"],
  ["wait", "a acompanhar", "à espera de outros", Clock, "border-slate-200 bg-slate-50", "text-slate-700", "text-slate-500"],
];

function Alerts({ n, pick, onPick }) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1" data-testid="gp-alerts">
      {ALERTS.map(([k, l, sub, Icon, look, txt, ic]) => (
        <button key={k} onClick={() => onPick(pick === k ? null : k)} data-testid={`gp-alert-${k}`}
          className={`text-left rounded-2xl border px-3 sm:px-4 py-3 flex items-center gap-3 transition-shadow hover:shadow-md ${look} ${pick === k ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}>
          <Icon className={`hidden sm:block shrink-0 ${ic}`} size={28} />
          <span className="min-w-0"><span className={`block text-base sm:text-xl font-semibold ${txt}`} data-testid={`gp-alert-${k}-n`}>{n[k]} {l}</span>
            <span className="hidden sm:block text-sm text-slate-500 leading-snug">{sub}</span></span>
        </button>
      ))}
    </div>
  );
}

export default function Painel() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [scope, setScope] = useState("meus");
  const [pick, setPick] = useState(null);
  const [fila, setFila] = useState(false);
  const [hl, setHl] = useState(null);
  const load = useCallback(() => api.get("/gestao/hoje", { params: { scope } }).then(({ data }) => setD(data)), [scope]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const groups = useMemo(() => (d ? groupTodo(d.todo) : null), [d]);
  useArrivalToast(scope === "meus" ? groups : null);
  if (!d) return <div className="py-20 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  const now = new Date();
  const open = (r) => {
    if (r.hl) { setHl(r); return; }
    api.post(`/gestao/processes/${r.pid}/seen`).catch(() => {}); nav(`/processo/${r.pid}`);
  };
  const remind = async (r) => {
    const b = r.remind;
    const ph = String(b.phone || "").replace(/\D/g, "");
    const sent = await remindAction({ pid: r.pid, token: b.token, phone: ph.length === 9 ? `351${ph}` : ph, button: b.label, reminder_keys: b.reminder_keys, reminder_docs: b.reminder_docs, reminder_doc: b.reminder_docs?.length === 1 ? b.reminder_docs[0] : null });
    if (sent) toast.success("Lembrete enviado");
    load();
  };
  const nudge = (r) => api.post(`/gestao/processes/${r.pid}/nudge`).then(() => { toast.success(r.consultant_f ? "Consultora avisada" : "Consultor avisado"); load(); }).catch(() => toast.error("Não foi possível avisar."));
  const urg = new Set(d.urgent || []);
  const isUrg = (g) => urg.has(g.pid);
  const n = { urgent: urg.size, today: groups.filter((g) => !isUrg(g)).length, wait: d.waiting.length };
  const shown = pick === "urgent" ? groups.filter(isUrg) : pick === "today" ? groups.filter((g) => !isUrg(g)) : pick === "wait" ? [] : groups;
  const waits = pick === "urgent" ? d.waiting.filter(isUrg) : d.waiting;
  const views = [["meus", `Os meus · ${d.mine}`], ["equipa", `Equipa · ${d.team_n}`], ["fila", `Sem gestor · ${d.queue}`]];
  return (
    <div className="space-y-5 pt-2 sm:pt-4" data-testid="painel-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl navy-text" data-testid="painel-hello">{hello()}, {d.name.split(" ")[0]}</h1>
          <p className="text-slate-500" data-testid="gp-sub">{WD[now.getDay()]}, {now.getDate()} de {MO[now.getMonth()]}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {views.map(([k, l]) => (
            <button key={k} onClick={() => (k === "fila" ? setFila(true) : setScope(k))} data-testid={`gp-scope-${k}`}
              className={`h-10 px-4 rounded-xl text-sm font-semibold border transition-colors ${scope === k ? "navy-bg text-white border-transparent" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Arrivals groups={groups} onOpen={open} />
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-5 items-start">
        <div className="space-y-3 min-w-0">
          <div className="flex flex-col xl:flex-row gap-3 pb-2"><Alerts n={n} pick={pick} onPick={setPick} /></div>
          {pick !== "wait" && <>
            <div className="flex items-baseline justify-between"><h2 className="text-lg font-semibold navy-text">A tratar</h2><span className="text-sm text-slate-500" data-testid="gp-today-count">{groups.some((g) => g.novo) ? "novos primeiro, depois pela data" : "ordenado pela data"} · {shown.length} {shown.length === 1 ? "processo" : "processos"}</span></div>
            {!shown.length && <p className="text-[15px] text-slate-500" data-testid="gp-today-empty">Nada contigo agora.</p>}
            {shown.map((g) => <ProcessCard key={g.pid} g={g} onOpen={open} />)}
          </>}
          {(!pick || pick === "wait" || pick === "urgent") && waits.length > 0 && <>
            <h2 className="text-lg font-semibold navy-text pt-2">À espera de outros</h2>
            <WaitList rows={waits} onRemind={remind} onNudge={nudge} />
          </>}
        </div>
        <div className="space-y-5"><WeekAgenda items={d.agenda} /><Compliance d={d} /></div>
      </div>
      <FilaDialog open={fila} onClose={() => setFila(false)} onChange={load} />
      {hl && <HoraLocalDialog r={hl} onClose={() => setHl(null)} onSaved={() => { setHl(null); load(); }} />}
    </div>
  );
}
